import { ChartingColors, Color } from '@persona-id/design-tokens';
import { groupBy, keyBy, reduce, startCase, sumBy, uniq } from 'lodash/fp';
import { ComponentProps, FC, memo, useCallback, useContext, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { ThemeContext } from 'styled-components';

import ChartLayout, {
  ChartLegendContent,
  ChartTooltipContainer,
  ChartTooltipDivider,
  ChartTooltipHeader,
  ChartTooltipLabel,
  ChartTooltipLabelContainer,
} from 'dashboard/layouts/ChartLayout';
import { Period, UnixTime } from 'dashboard/lib/unixTime';
import { generateTicks } from 'dashboard/lib/utils/charting';
import {
  formatChartDate,
  formatChartDateHour,
  formatChartDateHourChart,
  formatChartDateLong,
  formatChartDateLongWithDay,
  formatChartDateMonth,
  formatChartMonthName,
  formatNumber,
  formatNumberLarge,
} from 'dashboard/lib/utils/formatters';
import {
  CaseAnalyticsResolutionDistribution,
  ResolutionDistributionDatum,
} from 'dashboard/models/case-analytics';
import { CaseTemplateBasicsOptionState } from 'dashboard/models/case-template';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';
import { captureMessage } from 'lib/reporting';

export interface CaseResolutionDistributionChartProps extends CaseAnalyticsResolutionDistribution {
  allStates: CaseTemplateBasicsOptionState[];
}

const DEFAULT_DIMENSION_MARGIN = 16;

const DIMENSIONS = {
  height: 320,
  margin: {
    top: DEFAULT_DIMENSION_MARGIN * 2,
    left: -DEFAULT_DIMENSION_MARGIN,
  },
};

const CaseResolutionDistributionChart = ({
  data,
  domain,
  period,
  allStates,
}: CaseResolutionDistributionChartProps) => {
  const theme = useContext(ThemeContext);

  const [filteredStates, setFilteredStates] = useState<string[]>([]);

  const [legendHovered, { enable: setLegendHovered, disable: clearLegendHovered }] = useToggle();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const handleLegendClick = useCallback(
    ({ dataKey }: Payload) =>
      typeof dataKey === 'string' &&
      setFilteredStates(
        filteredStates.includes(dataKey)
          ? filteredStates.filter((s) => s !== dataKey)
          : filteredStates.concat(dataKey),
      ),
    [filteredStates],
  );

  const handleLegendMouseEnter = useCallback(({ dataKey }: Payload) => {
    setLegendHovered();
    typeof dataKey === 'string' && setHoveredKey(dataKey);
  }, []);

  const handleLegendMouseLeave = useCallback(() => {
    clearLegendHovered();
    setHoveredKey(null);
  }, []);

  const stateMap = keyBy('token', allStates);

  const allDataStateNames: string[] = uniq(
    data.map((datum) => {
      // Fallback to unknown states and send sentry warning when state id is not matching
      if (!stateMap[datum.state_id]) {
        captureMessage('Unrecognized case state id', 'warning', {
          state_id: datum.state_id,
        });
      }
      return stateMap[datum.state_id]?.name || 'Unknown state';
    }),
  );

  const stateToColors = useMemo(() => {
    const stateHash: Record<string, string> = {};

    allDataStateNames.forEach((state, i) => {
      const colorIndex = i % ChartingColors.Categorical.length;
      stateHash[state] = ChartingColors.Categorical[colorIndex];
    });

    return stateHash;
  }, [allDataStateNames]);

  const filledData = useMemo(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => fillData(data, domain, period, allDataStateNames, stateMap),
    [data, domain, period, stateMap],
  );

  return (
    <ChartLayout>
      <ResponsiveContainer height={DIMENSIONS.height}>
        <BarChart data={filledData} margin={DIMENSIONS.margin}>
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            // Note we are using categorical x axis; this is because D3 doesn't handle time series bar charts well
            dataKey='result_timestamp'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={
              period === Period.Hour
                ? formatChartDateHourChart
                : period === Period.Month
                  ? formatChartMonthName
                  : formatChartDate
            }
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
          />
          <YAxis
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={formatNumberLarge}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          <Tooltip
            isAnimationActive={false}
            content={<TooltipContent period={period} hoveredKey={hoveredKey} />}
            allowEscapeViewBox={{
              x: true,
            }}
          />
          <Legend
            layout='vertical'
            verticalAlign='bottom'
            align='right'
            formatter={startCase}
            iconType='circle'
            inactiveColor={Color.CoreSlate50}
            // eslint-disable-next-line rulesdir/jsx-click-tracking-ids -- this element doesn't take ID
            onClick={handleLegendClick}
            onMouseEnter={handleLegendMouseEnter}
            onMouseLeave={handleLegendMouseLeave}
            content={<ChartLegendContent />}
          />
          {Object.keys(stateToColors).map((state: string) => (
            <Bar
              key={state}
              dataKey={state}
              stackId='caseResolution'
              onMouseEnter={() => setHoveredKey(state)}
              onMouseLeave={() => setHoveredKey(null)}
              fill={stateToColors[state]}
              fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === state ? 1 : 0.3}
              stroke={legendHovered && hoveredKey === state ? theme.color.border.active : undefined}
              hide={filteredStates.length > 0 && !filteredStates.includes(state)}
              style={{
                transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
              }}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartLayout>
  );
};

export default memo(CaseResolutionDistributionChart);

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const TooltipContent: FC<
  ComponentProps<typeof Tooltip> &
    Pick<CaseResolutionDistributionChartProps, 'period'> & { hoveredKey: string | null }
> = ({ active, payload, label, period, hoveredKey }) => {
  if (!active || payload == null) {
    return null;
  }

  const total = sumBy('value', payload);
  // reverse order to make items come in same order as chart bars
  const countLabels = payload
    .slice()
    .reverse()
    .map(({ color, dataKey, value }) =>
      typeof value === 'number' && value > 0 ? (
        <ChartTooltipLabelContainer
          key={dataKey}
          active={hoveredKey === dataKey}
          hasActive={hoveredKey != null}
        >
          <ChartTooltipLabel>
            <Dot style={{ color }} /> {startCase(String(dataKey))}
          </ChartTooltipLabel>
          <ChartTooltipLabel>{formatNumber(+value)}</ChartTooltipLabel>
          <ChartTooltipLabel>
            {formatNumber(+value / total, { style: 'percent' })}
          </ChartTooltipLabel>
        </ChartTooltipLabelContainer>
      ) : null,
    )
    .filter(Boolean);

  return (
    countLabels.length > 0 && (
      <ChartTooltipContainer>
        <ChartTooltipHeader>
          {period === 'week'
            ? `Week of ${formatChartDateLong(label)}`
            : period === 'month'
              ? formatChartDateMonth(label)
              : period === 'day'
                ? formatChartDateLongWithDay(label)
                : formatChartDateHour(label)}
        </ChartTooltipHeader>
        {countLabels}
        <ChartTooltipDivider />
        <ChartTooltipLabelContainer>
          <ChartTooltipLabel>Total</ChartTooltipLabel>
          <ChartTooltipLabel>{formatNumber(total)}</ChartTooltipLabel>
        </ChartTooltipLabelContainer>
      </ChartTooltipContainer>
    )
  );
};

interface ProcessedResolutionDatum extends Record<string, string | number> {
  result_timestamp: UnixTime;
}

const processData = (
  data: ResolutionDistributionDatum[],
  stateMap: Record<string, CaseTemplateBasicsOptionState>,
): ProcessedResolutionDatum[] => {
  const groupedData = groupBy('result_timestamp', data);
  const result = reduce(
    (result: ProcessedResolutionDatum[], value: ResolutionDistributionDatum[]) => {
      const states: Record<string, number> = {};
      value.forEach((datum) => {
        // Fallback to state_id when state id is not matching
        const stateName = stateMap[datum.state_id]?.name || 'Unknown state';
        if (Object.prototype.hasOwnProperty.call(states, stateName)) {
          states[stateName] += datum.count;
        } else {
          states[stateName] = datum.count;
        }
      });

      if (value.length > 0) {
        result.push({
          result_timestamp: value[0]['result_timestamp'],
          ...states,
        });
      }

      return result;
    },
    [],
    groupedData,
  );

  return result;
};

/**
 * Fills domain with placeholder entries spaced by period and merged with data.
 * This is needed to ensure there are no gaps in the categorical chart.
 * Recharts breaks when data are missing.
 *
 * https://github.com/recharts/recharts/issues/702#issuecomment-483767918
 *
 * We subtract 1 from the domain start to ensure the range is inclusive.
 */
const fillData = (
  data: ResolutionDistributionDatum[],
  domain: [number, number],
  period: Period,
  stateNames: string[],
  stateMap: Record<string, CaseTemplateBasicsOptionState>,
): ProcessedResolutionDatum[] => {
  const processedData = processData(data, stateMap);
  const ticks = generateTicks(period, domain);

  const filledData: ProcessedResolutionDatum[] = [];

  const placeholderData: Omit<ProcessedResolutionDatum, 'result_timestamp'> = reduce(
    (result: Omit<ProcessedResolutionDatum, 'result_timestamp'>, value: string) => {
      result[value] ||= 0;
      return result;
    },
    {},
    stateNames,
  );

  let dataIdx = 0;
  ticks.forEach((t) => {
    if (t === processedData[dataIdx]?.['result_timestamp']) {
      filledData.push(processedData[dataIdx]);
      dataIdx++;
    } else {
      filledData.push({
        result_timestamp: t,
        ...placeholderData,
      });
    }
  });

  return filledData;
};
